import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
  useLayoutEffect,
} from 'react';
import { AnimatePresence } from 'framer-motion';
import { message } from 'antd';
import { useBlurSafe } from '../../contexts/BlurContext';
import { useCompany } from '../../contexts/CompanyContext';

// API imports
import {
  deleteSpreadsheetRow,
  getPresetRequirements,
  fetchClients,
} from '../../api/mastersheetApi';

// Constants and styles
import { dropdownOptions } from './constants/dropdownOptions';
import {
  defaultColumnWidth,
  defaultRowHeight,
  datePickerStyles,
  spreadsheetStyles,
} from './constants/MasterSpreadsheetStyles';

// Utils
import { 
  formatDate, 
  getSafeValue, 
  normalizeQuarter, 
  getCellCoords,
  updateSpreadsheetCells,
} from './utils/helpers';

// Hooks
import { useSpreadsheetQuery } from './hooks/useSpreadsheetQuery';
import { useSpreadsheetSelection } from './hooks/useSpreadsheetSelection';
import { useSpreadsheetKeyboard } from './hooks/useSpreadsheetKeyboard';
import { useSpreadsheetMouse } from './hooks/useSpreadsheetMouse';
import { useSpreadsheetEditing } from './hooks/useSpreadsheetEditing';
import { useSpreadsheetClipboard } from './hooks/useSpreadsheetClipboard';
import { useSpreadsheetFiltering } from './hooks/useSpreadsheetFiltering';

// Services
import { spreadsheetExportService } from './services/spreadsheetExport';
import { aiRewriterService } from './services/aiRewriterService';

// Components
import SpreadsheetTable from './components/SpreadsheetTable';
import ActionButtons from './components/ActionButtons';
import CompletionModal from './components/CompletionModal';
import AIRewriterModal from './components/AIRewriterModal';
import RequirementRewriterModal from './components/RequirementRewriterModal';
import PresetRequirementsModal from './components/PresetRequirementsModal';
import ConfirmationModal from './components/ConfirmationModal';

const MasterSpreadsheet = ({
  year,
  currentSheetType,
  onToggleAutomaticEntry,
  onToggleRequirementsForm,
  onAddRow,
  initialColumnWidths,
}) => {
  // Initialize React Query hook for spreadsheet data
  const {
    data,
    columns: fetchedColumns,
    dropdownOptions: fetchedDropdownOptions,
    rowHeights: fetchedRowHeights,
    columnWidths: fetchedColumnWidths,
    isLoading,
    isError,
    error,
    refetch: refreshData,
    updateCells,
    undo,
    redo,
    deleteRows,
    isUpdating,
    isUndoing,
    isRedoing,
    isDeleting
  } = useSpreadsheetQuery(currentSheetType, year);

  // Get company context
  const { isCustomerView } = useCompany();

  const {
    selectionStart,
    setSelectionStart,
    selectionEnd,
    setSelectionEnd,
    isSelecting,
    setIsSelecting,
    activeCell,
    setActiveCell,
    editingCell,
    setEditingCell,
    editingValue,
    setEditingValue,
    copiedCells,
    setCopiedCells,
    copiedCellsRange,
    setCopiedCellsRange,
    isSelectingRows,
    setIsSelectingRows,
    selectEntireRow,
    selectAllCells,
    isCellSelected,
    getCellBorderStyle
  } = useSpreadsheetSelection();

  // State - Initialize column widths with initialColumnWidths if provided
  const [columnWidths, setColumnWidths] = useState(() => {
    return initialColumnWidths || {};
  });
  const [rowHeights, setRowHeights] = useState({});
  
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [isButtonContainerExpanded, setIsButtonContainerExpanded] = useState(true);
  const [buttonContainerHeight, setButtonContainerHeight] = useState(0);
  const [mounted, setMounted] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [showPresetRequirements, setShowPresetRequirements] = useState(false);
  const [presetRequirements, setPresetRequirements] = useState([]);
  const [selectedStakeholders, setSelectedStakeholders] = useState(['Plan Sponsor']);
  const [turnAllOff, setTurnAllOff] = useState(false);
  const [showAIRewriter, setShowAIRewriter] = useState(false);
  const [rewriteOption, setRewriteOption] = useState('Custom Rows');
  const [selectedRowNumbers, setSelectedRowNumbers] = useState([]);
  const [showRequirementRewriter, setShowRequirementRewriter] = useState(false);
  const [selectedRequirementRows, setSelectedRequirementRows] = useState([]);
  const [requirementRewriteOption, setRequirementRewriteOption] = useState('Custom Rows');
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [selectedRequirement, setSelectedRequirement] = useState(null);
  const [clients, setClients] = useState([]);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [deleteConfirmationData, setDeleteConfirmationData] = useState(null);

  // Refs
  const tableRef = useRef(null);
  const inputRef = useRef(null);
  const tableContainerRef = useRef(null);
  const buttonContainerRef = useRef(null);
  const replaceOnNextEdit = useRef(false);
  const cursorPositionRef = useRef(null);
  const selectRefs = useRef({});
  const columnSelectRef = useRef(null);

  // Platform detection
  const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;

  // Get blur context
  const { setIsBlurred } = useBlurSafe();

  // Visible columns logic
  const visibleColumnNames = useMemo(() => {
    // Use fetched columns from the API response, or extract from data
    const effectiveColumnNames = fetchedColumns && Object.keys(fetchedColumns).length > 0 
      ? fetchedColumns
      : (data && data.length > 0 ? Object.keys(data[0]).reduce((acc, key) => {
          acc[key] = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
          return acc;
        }, {}) : {});
    
    if (!effectiveColumnNames || Object.keys(effectiveColumnNames).length === 0) {
      return {};
    }
    const filtered = { ...effectiveColumnNames };
    delete filtered['unid'];
    delete filtered['Include Checklist'];
    delete filtered['clients_checked'];
    delete filtered['not_required'];
    delete filtered['AI Rewritten Requirement'];
    delete filtered['AI Rewritten Touchpoints'];
    delete filtered['_originalIndex'];
    return filtered;
  }, [fetchedColumns, data]);

  // Memoize the column keys to prevent infinite re-renders
  const visibleColumnKeys = useMemo(() => {
    return visibleColumnNames ? Object.keys(visibleColumnNames) : [];
  }, [visibleColumnNames]);

  const getVisibleColumns = useCallback(() => {
    const filteredColumns = {};
    
    // Always show all columns when none are selected
    if (!selectedColumns || selectedColumns.length === 0) {
      // Show all columns when no specific columns are selected
      Object.keys(visibleColumnNames).forEach(key => {
        filteredColumns[key] = visibleColumnNames[key];
      });
    } else {
      // First add Company Name if it exists
      if (visibleColumnNames['Company Name']) {
        filteredColumns['Company Name'] = visibleColumnNames['Company Name'];
      }
      // Then add selected columns
      selectedColumns.forEach(columnKey => {
        if (visibleColumnNames[columnKey] && columnKey !== 'Company Name') {
          filteredColumns[columnKey] = visibleColumnNames[columnKey];
        }
      });
    }
    
    return filteredColumns;
  }, [selectedColumns, visibleColumnNames]);

  // Scroll to cell helper - defined early as it's used by other hooks
  const scrollToCell = useCallback((row, col) => {
    if (tableRef.current) {
      const table = tableRef.current;
      const cell = table.querySelector(
        `tr:nth-child(${row + 3}) td:nth-child(${col + 2})`
      );
      if (cell) {
        cell.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'nearest',
        });
      }
    }
  }, []);

  // Initialize filtering hook
  const {
    filteredData,
    columnFilters,
    setColumnFilters,
    globalSearch,
    setGlobalSearch,
    sortColumn,
    sortDirection,
    columnFilterOptions,
    handleSort,
  } = useSpreadsheetFiltering({ data, visibleColumnNames });

  // Initialize editing hook
  const {
    enterEditMode,
    handleEditingChange,
    finishEditing,
    handleEditingKeyDown,
  } = useSpreadsheetEditing({
    data,
    filteredData,
    getVisibleColumns,
    visibleColumnNames,
    currentSheetType,
    year,
    updateCells,
    activeCell,
    setActiveCell,
    setSelectionStart,
    setSelectionEnd,
    scrollToCell,
    editingCell,
    setEditingCell,
    editingValue,
    setEditingValue,
    replaceOnNextEdit,
    cursorPositionRef,
  });

  // Initialize clipboard hook
  const {
    copySelectedCells,
    pasteSelectedCells,
    cutSelectedCells,
  } = useSpreadsheetClipboard({
    selectionStart,
    selectionEnd,
    activeCell,
    filteredData,
    data,
    currentSheetType,
    year,
    getVisibleColumns,
    updateCells,
  });

  // Cell movement helpers
  const moveActiveCell = useCallback((direction) => {
    if (!activeCell) {
      setActiveCell([0, 0]);
      setSelectionStart([0, 0]);
      setSelectionEnd([0, 0]);
      return;
    }

    const [row, col] = activeCell;
    let newRow = row;
    let newCol = col;

    switch (direction) {
      case 'ArrowUp':
        newRow = Math.max(0, row - 1);
        break;
      case 'ArrowDown':
        newRow = Math.min(filteredData.length - 1, row + 1);
        break;
      case 'ArrowLeft':
        newCol = Math.max(0, col - 1);
        break;
      case 'ArrowRight':
        newCol = Math.min(
          Object.keys(visibleColumnNames).length - 1,
          col + 1
        );
        break;
    }

    setActiveCell([newRow, newCol]);
    setSelectionStart([newRow, newCol]);
    setSelectionEnd([newRow, newCol]);
    scrollToCell(newRow, newCol);
  }, [activeCell, filteredData, visibleColumnNames, setActiveCell, setSelectionStart, setSelectionEnd, scrollToCell]);

  const extendSelection = useCallback((direction) => {
    if (!activeCell || !selectionEnd) return;

    const [endRow, endCol] = selectionEnd;
    let newEndRow = endRow;
    let newEndCol = endCol;

    switch (direction) {
      case 'ArrowUp':
        newEndRow = Math.max(0, endRow - 1);
        break;
      case 'ArrowDown':
        newEndRow = Math.min(filteredData.length - 1, endRow + 1);
        break;
      case 'ArrowLeft':
        newEndCol = Math.max(0, endCol - 1);
        break;
      case 'ArrowRight':
        newEndCol = Math.min(
          Object.keys(visibleColumnNames).length - 1,
          endCol + 1
        );
        break;
    }

    setSelectionEnd([newEndRow, newEndCol]);
    scrollToCell(newEndRow, newEndCol);
  }, [activeCell, selectionEnd, filteredData, visibleColumnNames, setSelectionEnd, scrollToCell]);

  // Delete operations
  const deleteSelectedCells = useCallback(async () => {
    if (!selectionStart || !selectionEnd) return;
    const [startRow, startCol] = selectionStart;
    const [endRow, endCol] = selectionEnd;
    const minRow = Math.max(0, Math.min(startRow, endRow));
    const maxRow = Math.min(filteredData.length - 1, Math.max(startRow, endRow));
    const minCol = Math.min(startCol, endCol);
    const maxCol = Math.max(startCol, endCol);
    
    const cellsToUpdate = [];
    const visibleCols = Object.keys(getVisibleColumns());
    
    for (let i = minRow; i <= maxRow; i++) {
      const rowData = filteredData[i];
      for (let j = minCol; j <= maxCol; j++) {
        const columnKey = visibleCols[j];
        cellsToUpdate.push({
          unid: rowData.unid,
          columnId: columnKey,
          value: ''
        });
      }
    }
    
    try {
      // Use the React Query mutation
      updateCells(cellsToUpdate);
    } catch (error) {
      console.error('Error deleting cells:', error);
      message.error('Failed to delete cells: ' + error.message);
    }
  }, [selectionStart, selectionEnd, filteredData, getVisibleColumns, updateCells]);

  const deleteSelectedRows = useCallback(async () => {
    if (!selectionStart || !selectionEnd) return;
    const startRow = Math.min(selectionStart[0], selectionEnd[0]);
    const endRow = Math.max(selectionStart[0], selectionEnd[0]);
    
    const rowsToDelete = [];
    for (let i = startRow; i <= endRow; i++) {
      if (filteredData[i]) {
        rowsToDelete.push(filteredData[i].unid);
      }
    }
    
    if (rowsToDelete.length === 0) return;
    
    // Store the data for deletion and show the custom modal
    setDeleteConfirmationData({
      rowsToDelete,
      rowCount: rowsToDelete.length
    });
    setShowDeleteConfirmation(true);
  }, [selectionStart, selectionEnd, filteredData]);

  // Handle the actual deletion after confirmation
  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteConfirmationData) return;
    
    try {
      // Use the React Query mutation
      deleteRows(deleteConfirmationData.rowsToDelete);
      
      setActiveCell(null);
      setSelectionStart(null);
      setSelectionEnd(null);
    } catch (error) {
      console.error('Error deleting rows:', error);
      message.error('Failed to delete rows: ' + error.message);
    }
  }, [deleteConfirmationData, deleteRows, setActiveCell, setSelectionStart, setSelectionEnd]);

  // Initialize mouse handling hook
  const {
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
  } = useSpreadsheetMouse({
    visibleColumnNames,
    filteredData,
    columnWidths,
    rowHeights,
    isSelecting,
    isSelectingRows,
    setColumnWidths,
    setRowHeights,
    setIsSelecting,
    setIsSelectingRows,
    setActiveCell,
    setSelectionStart,
    setSelectionEnd,
    setEditingCell,
    selectEntireRow,
    selectAllCells,
  });


  // Cell click handlers
  const handleCellClick = useCallback((rowIndex, colIndex) => {
    console.log('Cell clicked:', rowIndex, colIndex);
    if (editingCell && (editingCell[0] !== rowIndex || editingCell[1] !== colIndex)) {
      finishEditing();
    }
    // Set the clicked cell as active
    setActiveCell([rowIndex, colIndex]);
    setSelectionStart([rowIndex, colIndex]);
    setSelectionEnd([rowIndex, colIndex]);
    
    // Focus the table to ensure keyboard events work
    if (tableRef.current) {
      tableRef.current.focus();
    }
  }, [editingCell, finishEditing, setActiveCell, setSelectionStart, setSelectionEnd]);

  const handleCellDoubleClick = useCallback((rowIndex, colIndex) => {
    console.log('Cell double-clicked:', rowIndex, colIndex);
    // Prevent editing for customer users
    if (isCustomerView) {
      return;
    }
    enterEditMode(rowIndex, colIndex);
  }, [enterEditMode, isCustomerView]);

  // Column filter handler
  const handleFilterChange = useCallback((columnKey, value) => {
    setColumnFilters((prev) => ({
      ...prev,
      [columnKey]: value,
    }));
  }, [setColumnFilters]);

  // AI Rewriter handlers
  const handleAIRewrite = useCallback(async () => {
    await aiRewriterService.rewriteTouchpoints(
      year,
      rewriteOption,
      selectedRowNumbers,
      filteredData,
      () => {
        setShowAIRewriter(false);
        setSelectedRowNumbers([]);
        refreshData();
      }
    );
  }, [year, rewriteOption, selectedRowNumbers, filteredData, refreshData]);

  const handleRequirementRewrite = useCallback(async () => {
    await aiRewriterService.rewriteRequirements(
      year,
      requirementRewriteOption,
      selectedRequirementRows,
      filteredData,
      () => {
        setShowRequirementRewriter(false);
        setSelectedRequirementRows([]);
        refreshData();
      }
    );
  }, [year, requirementRewriteOption, selectedRequirementRows, filteredData, refreshData]);

  // Export handlers
  const handleExportToExcel = useCallback(() => {
    const filename = `${currentSheetType}_${year}_export`;
    const result = spreadsheetExportService.exportToExcel(filteredData, getVisibleColumns(), filename);
    if (result.success) {
      message.success('Data exported successfully');
    } else {
      message.error('Failed to export data: ' + result.error);
    }
  }, [filteredData, getVisibleColumns, currentSheetType, year]);

  const handleExportToCSV = useCallback(() => {
    const filename = `${currentSheetType}_${year}_export`;
    const result = spreadsheetExportService.exportToCSV(filteredData, getVisibleColumns(), filename);
    if (result.success) {
      message.success('Data exported successfully');
    } else {
      message.error('Failed to export data: ' + result.error);
    }
  }, [filteredData, getVisibleColumns, currentSheetType, year]);

  // Helper to check if company exists in client list
  const isCompanyInClientList = useCallback((companyName) => {
    if (!companyName || !clients || clients.length === 0) return false;
    
    return clients.some(client => {
      // Check both possible field names for company
      const clientCompanyName = client['Company Name'] || client.company_name || '';
      return clientCompanyName.toLowerCase() === companyName.toLowerCase();
    });
  }, [clients]);

  // Preset requirements handler
  const handlePresetRequirements = useCallback(async () => {
    try {
      const userId = localStorage.getItem('userid');
      const requirements = await getPresetRequirements(userId);
      setPresetRequirements(requirements);
      setShowPresetRequirements(true);
    } catch (error) {
      message.error('Failed to load preset requirements');
    }
  }, []);

  // Effects
  useEffect(() => {
    setMounted(true);
  }, []);

  // Manage blur state when modals are open
  useEffect(() => {
    const isAnyModalOpen = showDeleteConfirmation || showCompletionModal || 
                          showPresetRequirements || showAIRewriter || 
                          showRequirementRewriter;
    setIsBlurred(isAnyModalOpen);
  }, [showDeleteConfirmation, showCompletionModal, showPresetRequirements, 
      showAIRewriter, showRequirementRewriter, setIsBlurred]);

  // Initialize column widths and row heights when data is loaded
  useEffect(() => {
    if (visibleColumnKeys.length > 0) {
      setColumnWidths(prevWidths => {
        const newWidths = {};
        let hasChanges = false;
        
        visibleColumnKeys.forEach((key) => {
          // Priority: existing width > initial width > fetched width > default
          const width = prevWidths[key] || 
                       initialColumnWidths?.[key] || 
                       fetchedColumnWidths?.[key] || 
                       defaultColumnWidth;
          newWidths[key] = width;
          
          // Check if this is actually a change
          if (prevWidths[key] !== width) {
            hasChanges = true;
          }
        });
        
        // Only update if there are actual changes
        return hasChanges ? newWidths : prevWidths;
      });
    }

    if (data && data.length > 0) {
      setRowHeights(prevHeights => {
        const newHeights = {};
        let hasChanges = false;
        
        data.forEach((_, index) => {
          const height = prevHeights[index] || fetchedRowHeights?.[index] || defaultRowHeight;
          newHeights[index] = height;
          
          if (prevHeights[index] !== height) {
            hasChanges = true;
          }
        });
        
        // Only update if there are actual changes
        return hasChanges ? newHeights : prevHeights;
      });
    }
  }, [data, visibleColumnKeys, fetchedColumnWidths, fetchedRowHeights, initialColumnWidths]);

  // Reset selected columns when spreadsheet type changes
  useEffect(() => {
    setSelectedColumns([]);
  }, [currentSheetType]);

  useLayoutEffect(() => {
    if (buttonContainerRef.current) {
      setButtonContainerHeight(buttonContainerRef.current.offsetHeight);
    }
  }, [isButtonContainerExpanded]);

  useEffect(() => {
    const fetchClientsData = async () => {
      try {
        const userId = localStorage.getItem('userid');
        if (userId && year) {
          const clientsData = await fetchClients(userId, year);
          setClients(clientsData || []);
        }
      } catch (error) {
        console.error('Error fetching clients:', error);
        setClients([]);
      }
    };
    fetchClientsData();
  }, [year]);

  // Initialize keyboard handling hook (after all handlers are defined)
  useSpreadsheetKeyboard({
    activeCell,
    editingCell,
    visibleColumnNames,
    selectionStart,
    selectionEnd,
    filteredData,
    isMac,
    enterEditMode,
    finishEditing,
    setActiveCell,
    setSelectionStart,
    setSelectionEnd,
    scrollToCell,
    extendSelection,
    moveActiveCell,
    copySelectedCells,
    cutSelectedCells,
    pasteSelectedCells,
    deleteSelectedCells,
    deleteSelectedRows,
    selectAllCells,
    isCustomerView,
    undo,
    redo,
    toggleButtonContainer: () => setIsButtonContainerExpanded(!isButtonContainerExpanded),
    downloadAllSpreadsheets: handleExportToExcel,
    onToggleAutomaticEntry,
  });

  // Styles
  useEffect(() => {
    const styleSheet = document.createElement('style');
    styleSheet.type = 'text/css';
    styleSheet.innerText = datePickerStyles + spreadsheetStyles;
    document.head.appendChild(styleSheet);
    
    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);


  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-lg">Loading spreadsheet data...</div>
      </div>
    );
  }

  // Error state
  if (isError) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-red-500">
          Error loading spreadsheet data: {error?.message || 'Unknown error'}
        </div>
      </div>
    );
  }

  return (
    <div className={`flex flex-col h-screen ${isFullScreen ? 'fixed inset-0 z-50 bg-gradient-to-br from-gray-50 to-blue-50' : 'relative'} p-6`}>
      <AnimatePresence mode="wait">
        {mounted && (
          <>
            <div className="flex-1 overflow-hidden relative flex flex-col">
              <SpreadsheetTable
                ref={tableRef}
                filteredData={filteredData}
                columnWidths={columnWidths}
                rowHeights={rowHeights}
                visibleColumnNames={visibleColumnNames}
                buttonContainerHeight={buttonContainerHeight}
                isButtonContainerExpanded={isButtonContainerExpanded}
                activeCell={activeCell}
                selectionStart={selectionStart}
                selectionEnd={selectionEnd}
                editingCell={editingCell}
                editingValue={editingValue}
                sortColumn={sortColumn}
                sortDirection={sortDirection}
                columnFilters={columnFilters}
                columnFilterOptions={columnFilterOptions}
                getVisibleColumns={getVisibleColumns}
                handleSort={handleSort}
                handleFilterChange={handleFilterChange}
                handleCellClick={handleCellClick}
                handleCellDoubleClick={handleCellDoubleClick}
                handleEditingChange={handleEditingChange}
                finishEditing={finishEditing}
                handleEditingKeyDown={handleEditingKeyDown}
                enterEditMode={enterEditMode}
                isCellSelected={isCellSelected}
                getCellBorderStyle={getCellBorderStyle}
                setEditingValue={setEditingValue}
                setEditingCell={setEditingCell}
                setSelectionStart={setSelectionStart}
                setSelectionEnd={setSelectionEnd}
                setActiveCell={setActiveCell}
                updateCells={updateCells}
                currentSheetType={currentSheetType}
                year={year}
                selectRefs={selectRefs}
                isCompanyInClientList={isCompanyInClientList}
                setSelectedRequirement={setSelectedRequirement}
                setShowCompletionModal={setShowCompletionModal}
                replaceOnNextEdit={replaceOnNextEdit}
                handleMouseDown={handleMouseDown}
                handleMouseMove={handleMouseMove}
                handleMouseUp={handleMouseUp}
              />
            </div>

            <div
              ref={buttonContainerRef}
              className="absolute bottom-6 left-6 right-6 z-30 bg-gradient-to-br from-slate-50/95 via-white/95 to-slate-50/95 backdrop-blur-md border border-slate-200/50 shadow-lg rounded-2xl"
            >
              <ActionButtons
                isButtonContainerExpanded={isButtonContainerExpanded}
                toggleButtonContainer={() => setIsButtonContainerExpanded(!isButtonContainerExpanded)}
                activeCell={activeCell}
                selectionStart={selectionStart}
                selectionEnd={selectionEnd}
                editingCell={editingCell}
                onToggleAutomaticEntry={onToggleAutomaticEntry}
                onToggleRequirementsForm={onToggleRequirementsForm}
                currentSheetType={currentSheetType}
                handleFetchPresetRequirements={handlePresetRequirements}
                undo={undo}
                redo={redo}
                deleteSelectedRows={deleteSelectedRows}
                isCustomerView={isCustomerView}
                downloadAllSpreadsheets={() => {
                  // TODO: Implement download all spreadsheets
                  message.info('Download all spreadsheets feature coming soon');
                }}
                clearAllFilters={() => {
                  setColumnFilters({});
                  setGlobalSearch('');
                  message.success('All filters cleared');
                }}
                hasActiveFilters={Object.keys(columnFilters).length > 0 || globalSearch !== ''}
                globalSearch={globalSearch}
                setGlobalSearch={setGlobalSearch}
                selectedColumns={selectedColumns}
                setSelectedColumns={setSelectedColumns}
                visibleColumnNames={visibleColumnNames}
                columnSelectRef={columnSelectRef}
              />
            </div>
          </>
        )}
      </AnimatePresence>

      {showCompletionModal && selectedRequirement && (
        <CompletionModal
          show={showCompletionModal}
          onClose={() => {
            setShowCompletionModal(false);
            setSelectedRequirement(null);
          }}
          requirement={selectedRequirement}
          currentSheetType={currentSheetType}
          year={year}
          clients={clients}
          refreshData={refreshData}
        />
      )}


      {showPresetRequirements && (
        <PresetRequirementsModal
          show={showPresetRequirements}
          onClose={() => setShowPresetRequirements(false)}
          presetRequirements={presetRequirements}
          setPresetRequirements={setPresetRequirements}
          selectedStakeholders={selectedStakeholders}
          setSelectedStakeholders={setSelectedStakeholders}
          turnAllOff={turnAllOff}
          setTurnAllOff={setTurnAllOff}
        />
      )}

      <AIRewriterModal
        show={showAIRewriter}
        onClose={() => setShowAIRewriter(false)}
        filteredData={filteredData}
        rewriteOption={rewriteOption}
        setRewriteOption={setRewriteOption}
        selectedRowNumbers={selectedRowNumbers}
        setSelectedRowNumbers={setSelectedRowNumbers}
        onRewrite={handleAIRewrite}
      />

      <RequirementRewriterModal
        show={showRequirementRewriter}
        onClose={() => setShowRequirementRewriter(false)}
        filteredData={filteredData}
        rewriteOption={requirementRewriteOption}
        setRewriteOption={setRequirementRewriteOption}
        selectedRowNumbers={selectedRequirementRows}
        setSelectedRowNumbers={setSelectedRequirementRows}
        onRewrite={handleRequirementRewrite}
      />

      <ConfirmationModal
        show={showDeleteConfirmation}
        onClose={() => {
          setShowDeleteConfirmation(false);
          setDeleteConfirmationData(null);
        }}
        onConfirm={handleDeleteConfirm}
        title={deleteConfirmationData?.rowCount === 1 ? 'Delete Row' : `Delete ${deleteConfirmationData?.rowCount || 0} Rows`}
        message={deleteConfirmationData?.rowCount === 1 
          ? 'Are you sure you want to delete this row?'
          : `Are you sure you want to delete ${deleteConfirmationData?.rowCount || 0} rows?`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  );
};

export default MasterSpreadsheet;